import React, { useEffect, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import CommonModal from "../based/Modal";
import { Notify } from "../based/Notify";
import { NOTIFY, WalletTransactionType, WalletWithdrawStatus } from "../based/Constants";
import WithdrawalCancelModal from "./WithdrawalCancelModal";
import FinanceServices from "../based/services/FinanceServices";

const MODAL_ID = "withdrawal-detail-modal";

const STATUS_META = {
    [WalletWithdrawStatus.PENDING]: { label: "Khách yêu cầu", bg: "#ad7c3d", color: "#fff" },
    [WalletWithdrawStatus.APPROVED]: { label: "Đợi tạo lệnh", bg: "#ff9027", color: "#fff" },
    [WalletWithdrawStatus.PROCESSING]: { label: "Đang duyệt chi", bg: "#2cb0fb", color: "#fff" },
    [WalletWithdrawStatus.COMPLETED]: { label: "Ngân hàng xử lý", bg: "#9e9e9e", color: "#fff" },
    [WalletWithdrawStatus.CANCELED]: { label: "Thành công", bg: "#28b259", color: "#fff" },
    [WalletWithdrawStatus.REJECTED]: { label: "Đã hủy", bg: "#e9e9e9", color: "#444" },
};

function formatMoney(amount) {
    if (!amount && amount !== 0) return "—";
    return new Intl.NumberFormat("vi-VN").format(amount) + "đ";
}

function formatDateTime(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")} ${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}`;
}

export default function WithdrawalDetailModal({ show, requestId, onClose, onStatusChanged }) {
    const [detail, setDetail] = useState(null);
    const [isBusy, setBusy] = useState(false);
    const [showCancel, setShowCancel] = useState(false);

    useEffect(() => {
        if (show && requestId) loadDetail();
        else setDetail(null);
    }, [show, requestId]);

    async function loadDetail() {
        setBusy(true);
        const [err, data] = await FinanceServices.GetAdminWithdrawalRequestsById(requestId);
        setBusy(false);
        if (!err && data) {
            setDetail({
                ...data,
                bankInfo: data.bankName || data.accountNumber ? { bankName: data.bankName, accountNumber: data.accountNumber, accountName: data.accountName } : null,
            });
        }
    }

    async function handleMoveToWaiting() {
        setBusy(true);
        const [err] = await FinanceServices.AdminApproveWithdrawalRequests({ ids: [requestId] });
        setBusy(false);
        if (err) {
            Notify(NOTIFY.ERROR, "Lỗi", "Không thể chuyển trạng thái. Vui lòng thử lại.");
            return;
        }
        Notify(NOTIFY.SUCCESS, "Thành công", "Đã chuyển sang Đợi tạo lệnh");
        onStatusChanged && onStatusChanged();
        onClose();
    }

    const statusMeta = detail ? STATUS_META[detail.status] : null;
    const canMoveToWaiting = detail && detail.status === WalletWithdrawStatus.PENDING;
    const canCancel = detail && (detail.status === WalletWithdrawStatus.PENDING || detail.status === WalletWithdrawStatus.APPROVED);

    return (
        <>
            <ModalStyle />
            <CommonModal
                id={MODAL_ID}
                show={show && !showCancel}
                title="Chi tiết"
                size="lg"
                isBusy={isBusy}
                busyMsg="Đang tải..."
                onClose={onClose}
                hiddenFooter={false}
                hideOnClose={true}
                customButton={
                    <div className="d-flex">
                        <button className="btn btn-detail-close mr-2" onClick={onClose}>
                            Đóng
                        </button>
                        {canCancel && (
                            <button className="btn btn-detail-cancel mr-2" onClick={() => setShowCancel(true)}>
                                Hủy yêu cầu
                            </button>
                        )}
                        {canMoveToWaiting && (
                            <button className="btn btn-detail-approve" onClick={handleMoveToWaiting} disabled={isBusy}>
                                Chuyển Đợi lệnh
                            </button>
                        )}
                    </div>
                }
            >
                {!detail ? (
                    <div className="text-center p-4">Đang tải dữ liệu...</div>
                ) : (
                    <Wrapper>
                        <div className="row">
                            {/* Left panel */}
                            <div className="col-md-5 border-right pr-4">
                                <div className="mb-3 d-flex align-items-center">
                                    <span className="detail-label d-block">Trạng thái</span>
                                    {statusMeta && (
                                        <span className="status-pill ml-3" style={{ background: statusMeta.bg, color: statusMeta.color }}>
                                            {statusMeta.label}
                                        </span>
                                    )}
                                </div>

                                <div className="mb-3 d-flex align-items-center">
                                    <span className="detail-label d-block">Số tiền rút</span>
                                    <strong className="amount-text ml-3">{formatMoney(detail.amount)}</strong>
                                </div>

                                <hr />

                                <div className="row mb-2">
                                    <span className="col-5 detail-label">Khách hàng</span>
                                    <span className="col-7 font-weight-bold">{detail.customerName || "—"}</span>
                                </div>
                                <div className="row mb-2 align-items-center">
                                    <span className="col-5 detail-label">SĐT</span>
                                    <span className="col-4">{detail.phone || "—"}</span>
                                    {detail.phone && (
                                        <a className="zalo-btn" href={`https://zalo.me/${detail.phone}`} target="_blank" rel="noreferrer">
                                            Nhắn Zalo
                                        </a>
                                    )}
                                </div>
                                <div className="row mb-2">
                                    <span className="col-5 detail-label">Email</span>
                                    <span className="col-7">{detail.email || "—"}</span>
                                </div>
                                <div className="row mb-3">
                                    <span className="col-5 detail-label">Đơn thành công</span>
                                    <span className="col-7">{detail.successOrderCount ?? "—"}</span>
                                </div>

                                {detail.bankInfo && (
                                    <div className="bank-card mb-3">
                                        <div className="row mb-1">
                                            <span className="col-5 detail-label">Ngân hàng</span>
                                            <span className="col-7 font-weight-bold">{detail.bankInfo.bankName}</span>
                                        </div>
                                        <div className="row mb-1">
                                            <span className="col-5 detail-label">STK</span>
                                            <span className="col-7">{detail.bankInfo.accountNumber}</span>
                                        </div>
                                        <div className="row">
                                            <span className="col-5 detail-label">Tên TK</span>
                                            <span className="col-7">{detail.bankInfo.accountName}</span>
                                        </div>
                                    </div>
                                )}

                                <div className="d-flex flex-wrap">
                                    {detail.withdrawCount != null && <span className="tag tag-grey">{detail.withdrawCount} lần rút</span>}
                                    {detail.isNewAccount && (
                                        <span className="tag tag-red">
                                            <i className="fa fa-play fa-icon-sm" />
                                            Tài khoản mới
                                        </span>
                                    )}
                                    {detail.isDifferentAccount && (
                                        <span className="tag tag-red">
                                            <i className="fa fa-play fa-icon-sm" />
                                            STK khác lần cuối giao dịch
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Right panel */}
                            <div className="col-md-7 pl-4">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <strong className="section-title">Lịch sử giao dịch ví của khách</strong>
                                    <span className="detail-label">
                                        Số dư hiện tại: <strong className="balance-value">{formatMoney(detail.currentBalance)}</strong>
                                    </span>
                                </div>
                                <table className="table table-sm table-bordered mb-4 detail-table-sm">
                                    <thead>
                                        <tr>
                                            <th>Thời gian</th>
                                            <th>Giao dịch</th>
                                            <th className="text-right">Số tiền</th>
                                            <th className="text-right">Số dư</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detail.walletTransactions && detail.walletTransactions.length > 0 ? (
                                            detail.walletTransactions.map((item, idx) => {
                                                return (
                                                    <tr key={idx}>
                                                        <td className="text-nowrap">{formatDateTime(item.createdDate)}</td>
                                                        <td>
                                                            <div className="center-content">
                                                                {item.note}
                                                                {item.referenceCode && <div className="order-link">{item.referenceCode}</div>}
                                                            </div>
                                                        </td>
                                                        <td className={`text-right text-nowrap ${item.transactionType === WalletTransactionType.CREDIT ? "text-success" : "text-danger"}`}>
                                                            {item.transactionType === WalletTransactionType.CREDIT ? "+" : "-"}
                                                            {formatMoney(item.amount)}
                                                        </td>
                                                        <td className="text-right text-nowrap">{formatMoney(item.balanceAfter)}</td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={4} className="text-center text-muted">
                                                    Không có giao dịch
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                                {/* <strong className="section-title d-block mb-2">Lịch sử yêu cầu</strong>
                                <table className="table table-sm detail-table-sm">
                                    <tbody>
                                        {detail.requestHistory && detail.requestHistory.length > 0 ? (
                                            detail.requestHistory.map((h, idx) => (
                                                <tr key={idx}>
                                                    <td className="text-nowrap col-history-date detail-label">{formatDateTime(h.createdDate)}</td>
                                                    <td>{h.action}</td>
                                                    {h.cancelInfo && (
                                                        <td>
                                                            <div className="detail-label">Người hủy: {h.cancelInfo.canceledBy}</div>
                                                            <div className="detail-label">Lý do: {h.cancelInfo.reason}</div>
                                                            {h.cancelInfo.note && <div className="detail-label">Ghi chú: {h.cancelInfo.note}</div>}
                                                        </td>
                                                    )}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={2} className="text-center text-muted">
                                                    Chưa có lịch sử
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table> */}
                            </div>
                        </div>
                    </Wrapper>
                )}
            </CommonModal>

            <WithdrawalCancelModal
                show={showCancel}
                requestId={requestId}
                onClose={() => setShowCancel(false)}
                onSuccess={() => {
                    setShowCancel(false);
                    onStatusChanged && onStatusChanged();
                    onClose();
                }}
            />
        </>
    );
}

const ModalStyle = createGlobalStyle`
    #${MODAL_ID} .modal-header {
        background-color: #64c5b1;
        border-bottom: none;
    }
    #${MODAL_ID} .modal-title {
        color: #fff;
        font-weight: 600;
        font-size: 20px;
    }
    #${MODAL_ID} button.close-btn {
        color: #fff !important;
    }
    #${MODAL_ID} .btn-detail-close {
        background: #f96a74;
        color: #fff;
        min-width: 128px;
        height: 34px;
        font-size: 14px;
        font-weight: 600;
        border: none;
    }
    #${MODAL_ID} .btn-detail-cancel {
        background: #fff;
        color: #f96a74;
        border: 1px solid #f96a74;
        min-width: 128px;
        height: 34px;
        font-size: 14px;
        font-weight: 600;
    }
    #${MODAL_ID} .btn-detail-approve {
        background: #64c5b1;
        color: #fff;
        border: none;
        min-width: 128px;
        height: 34px;
        font-size: 14px;
        font-weight: 600;
    }
`;

const Wrapper = styled.div`
    .center-content {
        display: flex;
        align-items: center;
        font-size: 12px;
    }

    .center-content .order-link {
        margin-left: 4px;
    }

    .amount-text {
        font-size: 24px;
        font-weight: 700;
        color: #d70000;
    }
    .detail-label {
        font-size: 14px;
        color: #000;
        font-weight: 600;
    }
    .section-title {
        font-size: 14px;
        font-weight: 700;
        color: #444;
    }
    .balance-value {
        font-size: 14px;
        font-weight: 700;
        color: #444;
    }
    .status-pill {
        border-radius: 30px;
        padding: 4px 12px;
        font-size: 12px;
        display: inline-flex;
        align-items: center;
        height: 24px;
    }
    .bank-card {
        background: #ffcaca;
        border: 1px solid #d9d9d9;
        border-radius: 4px;
        padding: 12px;
    }
    .zalo-btn {
        background: #0067f4;
        color: #fff;
        border-radius: 80px;
        padding: 0 8px;
        font-size: 12px;
        white-space: nowrap;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        height: 22px;
    }
    .zalo-btn:hover {
        color: #fff;
        text-decoration: none;
    }
    .tag {
        border-radius: 6px;
        padding: 3px 8px;
        font-size: 12px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        margin-right: 4px;
        margin-bottom: 4px;
    }
    .tag-grey {
        background: #f1f1f1;
        border: 1px solid #ededed;
        color: #444;
    }
    .tag-red {
        background: #ffcaca;
        border: 1px solid #ededed;
        color: #d70000;
    }
    .fa-icon-sm {
        font-size: 10px;
    }
    .detail-table-sm {
        font-size: 13px;
    }
    .detail-table-sm thead th {
        background: #d9d9d9;
        font-weight: 600;
        font-size: 14px;
        color: #444;
        border-color: #d9d9d9;
    }
    .order-link {
        font-size: 12px;
        font-weight: 600;
        color: #13aaff;
    }
    .order-link:hover {
        color: #0090e0;
        text-decoration: none;
    }
    .col-history-date {
        width: 150px;
    }
`;
